import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Utensils, Truck, HeartHandshake, CheckCircle2, Loader2, Cpu } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SiteHeader } from "../components/SiteHeader";
import { openBooking } from "../components/BookingDialog";
import { PatientStories } from "../components/PatientStories";
import { API } from "@/App";
import { toast } from "sonner";

const SERVICES = [
  { icon: Stethoscope, title: "One-on-One Nutrition Consultations", desc: "Work with a dietitian to understand your nutritional needs, discuss your health goals and develop a practical plan tailored to your circumstances." },
  { icon: Utensils, title: "Personalised Meal Plans", desc: "Get meal plans designed around your health needs, food preferences, cultural background, budget and daily routine, with practical guidance on portions, ingredients and meal choices." },
  { icon: Truck, title: "Healthy Meal Delivery", desc: "Make healthy eating more practical with prepared meals designed to support your nutritional needs and health goals, subject to service availability." },
  { icon: HeartHandshake, title: "Integrated Doctor and Dietitian Care", desc: "Nutrition works best when it complements the wider healthcare journey. Where coordinated care arrangements are available, we work with patients and their treating clinicians to align nutrition support with their broader treatment plans." },
];

const APPROACH = [
  { num: "01", title: "Clinically informed", desc: "Our approach draws on nutrition science and individual assessment to provide recommendations that reflect each person's health needs." },
  { num: "02", title: "Culturally relevant", desc: "Food is personal. We recognise the importance of familiar meals, cultural traditions and food preferences when developing practical nutrition guidance." },
  { num: "03", title: "Designed for everyday life", desc: "Nutrition plans should work beyond the consultation room. We consider food availability, affordability, routines and the challenges of maintaining healthy habits." },
  { num: "04", title: "Supported by technology", desc: "We are developing AI-supported nutritional management tools to help make nutrition guidance more responsive and personalised, with appropriate human oversight." },
];

const CONDITIONS = [
  "Diabetes and blood sugar management",
  "Weight management and metabolic health",
  "Cardiovascular health and related risk factors",
  "Personalised nutrition for people living with multiple health conditions",
];

const Eyebrow = ({ children }) => (
  <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-4">{children}</span>
);

const H2 = ({ children, testid }) => (
  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-[1.15] tracking-tight text-[#1C1C1C]" data-testid={testid}>{children}</h2>
);

const TechSection = () => (
  <section className="bg-white" data-testid="technology-section">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-16">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
        <div>
          <Eyebrow>Technology with a human-centred approach</Eyebrow>
          <H2 testid="technology-heading">Making personalised nutrition more responsive.</H2>
        </div>
        <div className="space-y-5 text-base text-stone-500 leading-relaxed">
          <p>We believe technology can help nutrition care become more accessible, practical and relevant to individual needs.</p>
          <p>PlateMind is developing AI-supported tools that connect food choices with nutritional information and cardiometabolic health goals. Our longer-term vision is to make nutrition support more responsive to the health needs and everyday realities of African immigrants.</p>
          <div className="flex gap-3 items-start p-5 rounded-2xl bg-[#FFF8F2] border border-[#FF7A00]/10" data-testid="technology-disclaimer">
            <Cpu className="w-5 h-5 text-[#FF7A00] shrink-0 mt-0.5" />
            <p className="text-sm text-stone-600">Technology is intended to support informed decisions and professional care, not replace the judgement of qualified healthcare professionals.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhoWeSupport = () => (
  <section id="who-we-support" className="bg-[#FAFAF7]" data-testid="support-section">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-16">
      <div className="max-w-[720px]">
        <Eyebrow>Who we support</Eyebrow>
        <H2 testid="support-heading">Nutrition support for the health challenges that matter.</H2>
        <p className="mt-5 text-base text-stone-500 leading-relaxed">PlateMind focuses on nutrition and lifestyle support for people managing cardiometabolic health concerns, including:</p>
      </div>
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        {CONDITIONS.map((c, i) => (
          <div key={c} className="flex gap-3 items-start p-5 rounded-2xl bg-white border border-stone-100" data-testid={`support-item-${i + 1}`}>
            <CheckCircle2 className="w-5 h-5 text-[#FF7A00] shrink-0 mt-0.5" />
            <span className="text-sm font-semibold text-[#1C1C1C] leading-relaxed">{c}</span>
          </div>
        ))}
      </div>
      <p className="mt-6 text-sm text-stone-400 leading-relaxed">Recommendations are tailored to individual needs and delivered within the appropriate clinical scope.</p>
    </div>
  </section>
);

const FinalCTA = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, source: "homepage" }) });
      const d = await r.json();
      if (!r.ok) { toast.error((Array.isArray(d.detail) ? d.detail[0]?.msg?.replace("Value error, ", "") : d.detail) || "Could not subscribe"); return; }
      setDone(d.message || "Thank you for subscribing!"); setEmail("");
    } catch { toast.error("Connection error. Please try again."); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white" data-testid="final-cta-section">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pt-2 pb-14 sm:pb-20">
        <div className="max-w-[760px] mx-auto text-center rounded-3xl bg-[#FFF8F2] border border-[#FF7A00]/10 px-6 sm:px-12 py-12 sm:py-14">
          <H2 testid="final-cta-heading">Let's make nutrition work for your life.</H2>
          <p className="mt-4 text-base text-stone-500 leading-relaxed max-w-[580px] mx-auto">Whether you are looking for professional nutrition guidance, a personalised meal plan or a partnership to extend nutrition care to your community, PlateMind is here to help you take the next step.</p>
          {done ? (
            <p className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-green-700" data-testid="newsletter-success"><CheckCircle2 className="w-4 h-4" />{done}</p>
          ) : (
            <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-[480px] mx-auto" data-testid="newsletter-form">
              <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" className="h-12 flex-1 min-w-0 rounded-full bg-white border-stone-200 px-5" data-testid="newsletter-email" />
              <Button type="submit" disabled={busy} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-7 h-12 text-[15px] font-semibold shrink-0" data-testid="newsletter-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Subscribe to our newsletter
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const WHATSAPP_ENQUIRY = "https://wa.me/2347062039281?text=Hello%2C%20I%27d%20like%20to%20learn%20more%20about%20PlateMind.";

const HomePage = () => {
  const scrollTo = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <SiteHeader />

      {/* ===== HERO ===== */}
      <section className="pt-16 bg-gradient-to-b from-[#FFF8F2] to-white" data-testid="hero-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pt-12 sm:pt-16 lg:pt-20">
          {/* Text */}
          <div className="text-center max-w-[780px] mx-auto">
            <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-4" data-testid="hero-eyebrow">
              Cardiometabolic Nutrition Care, Powered by AI
            </span>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-extrabold leading-[1.12] tracking-tight text-[#1C1C1C]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="hero-headline"
            >
              Nutrition that understands your health, your food and your life.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-stone-500 leading-relaxed max-w-[640px] mx-auto" data-testid="hero-copy">
              PlateMind combines professional nutrition care, culturally relevant food guidance and AI-supported nutritional management to support better cardiometabolic health. We make nutrition more personal, practical and responsive to the realities of everyday life, with a focus on African and African diaspora food cultures.
            </p>
          </div>

          {/* Hero product image */}
          <div className="mt-10 sm:mt-14">
            <img
              src="/hero-patient-experience.webp"
              alt="PlateMind patient experience: personalised meal plan, progress tracking, patient app and video consultation with a doctor"
              className="w-full h-auto"
              loading="eager"
              data-testid="hero-image"
            />
          </div>

          {/* CTAs below image */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <Button
                onClick={openBooking}
                className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-7 h-12 text-[15px] font-semibold shadow-lg shadow-orange-200/30 w-full sm:w-auto"
                data-testid="hero-primary-cta"
              >
                Book a Consultation
              </Button>
            <a href={WHATSAPP_ENQUIRY} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="rounded-full px-7 h-12 text-[15px] font-semibold border-stone-200 text-stone-600 hover:bg-stone-50 w-full sm:w-auto"
                data-testid="hero-secondary-cta"
              >
                Make an Enquiry
              </Button>
            </a>
          </div>
          <p className="mt-4 text-center text-sm text-stone-500 pb-4 sm:pb-6" data-testid="hero-support-text">
            Personalised nutrition care, informed by science and designed around real life.
          </p>
        </div>
      </section>

      {/* ===== OUR SERVICES ===== */}
      <section id="platform" className="bg-white scroll-mt-16" data-testid="services-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-8 sm:py-12 lg:py-14">
          <div className="text-center max-w-[660px] mx-auto mb-12 sm:mb-16">
            <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-4">
              Our Services
            </span>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-[1.15] tracking-tight text-[#1C1C1C]"
              data-testid="services-heading"
            >
              Nutrition care designed around you.
            </h2>
            <p className="mt-4 text-base text-stone-500 leading-relaxed">
              Whether you need professional guidance, a personalised meal plan or ongoing support, PlateMind offers services designed to help you make informed decisions about your health.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {SERVICES.map((feature, i) => (
              <div
                key={i}
                className="p-6 sm:p-7 rounded-2xl border border-stone-100 hover:border-[#FF7A00]/20 hover:bg-[#FFF8F2] transition-all duration-300 group"
                data-testid={`service-card-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center mb-4 group-hover:bg-[#FF7A00]/15 transition-colors">
                  <feature.icon className="w-5 h-5 text-[#FF7A00]" />
                </div>
                <h3 className="text-base font-bold text-[#1C1C1C] mb-2">{feature.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== OUR APPROACH ===== */}
      <section id="approach" className="bg-[#FAFAF7] scroll-mt-16" data-testid="approach-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-4">
                Our Approach
              </span>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-[1.15] tracking-tight text-[#1C1C1C]"
                data-testid="approach-heading"
              >
                Personalised nutrition. Intelligent technology. Human understanding.
              </h2>
              <p className="mt-5 text-base text-stone-500 leading-relaxed">
                Food choices are shaped by culture, affordability, availability, personal preferences and the realities of everyday life. For African immigrants navigating different food environments, nutrition guidance needs to account for more than generic recommendations. PlateMind is being designed to bring these realities into personalised cardiometabolic nutrition support.
              </p>

              <div className="mt-8 space-y-6">
                {APPROACH.map((item, i) => (
                  <div key={i} className="flex gap-4 items-start" data-testid={`approach-principle-${i + 1}`}>
                    <span className="text-xs font-bold text-[#FF7A00] bg-[#FF7A00]/10 rounded-full w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                      {item.num}
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-[#1C1C1C]">{item.title}</h3>
                      <p className="mt-1 text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Product image */}
            <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl shadow-stone-200/40 border border-stone-100">
              <img
                src="/platemind-connected-care.png"
                alt="PlateMind connected care workflow showing patient and dietitian interaction, automated booking, WhatsApp integration and clinical dashboard"
                className="w-full h-auto"
                loading="lazy"
                data-testid="approach-image"
              />
            </div>
          </div>
        </div>
      </section>

      <TechSection />
      <WhoWeSupport />
      <PatientStories limit={2} />
      <FinalCTA />

      {/* ===== IN-HOUSE PROGRAMME (brief mention) ===== */}
      <section className="bg-[#1C1C1C]" data-testid="programme-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-14 sm:py-20 lg:py-24">
          <div className="max-w-[680px] mx-auto text-center">
            <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00] mb-4">
              Get a Personalised Nutrition Care Plan
            </span>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.2rem] font-extrabold leading-[1.15] tracking-tight text-white"
              data-testid="programme-heading"
            >
              Start with a more personalised approach to your health.
            </h2>
            <p className="mt-4 text-base text-white/55 leading-relaxed max-w-[540px] mx-auto" data-testid="programme-copy">
              The PlateMind 12-week programme offers structured nutrition support shaped around your health needs, food preferences and everyday life, with guidance from a dietitian.
            </p>
            <div className="mt-7">
              <Link to="/programme">
                <Button className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-7 h-11 text-sm font-semibold" data-testid="programme-cta">
                  Explore the Programme
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#141414]" data-testid="footer">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-14">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-8">
            <div className="max-w-[340px]">
              <img src="/platemind-logo-new.png" alt="PlateMind" className="h-7 w-auto brightness-0 invert mb-4" data-testid="footer-logo" />
              <p className="text-sm text-white/45 leading-relaxed">
                AI-supported nutritional management for personalised cardiometabolic health.
              </p>
              <p className="mt-3 text-sm text-white/30">A TNN Africa product.</p>
            </div>

            <div className="flex gap-12 sm:gap-16">
              <div>
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Product</p>
                <div className="space-y-2">
                  <button onClick={() => scrollTo("platform")} className="block text-sm text-white/40 hover:text-white transition-colors">Platform</button>
                  <button onClick={() => scrollTo("approach")} className="block text-sm text-white/40 hover:text-white transition-colors">Our Approach</button>
                  <Link to="/programme" className="block text-sm text-white/40 hover:text-white transition-colors">12-Week Programme</Link>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Legal</p>
                <div className="space-y-2">
                  <Link to="/privacy" className="block text-sm text-white/40 hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="block text-sm text-white/40 hover:text-white transition-colors">Terms of Service</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/8">
            <p className="text-xs text-white/12">
              &copy; {new Date().getFullYear()} PlateMind. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
